import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { ChevronRight } from "lucide-react";
import { CheckList, MailActivityDashboard, StatusPill, type CheckItem } from "@ozpanel/kit";
import { Details, Section, StateView, Technical, cn, formatBytes, useAuth } from "@ozpanel/ui";
import { useMailboxApi } from "../api";

// The mailbox at a glance — ONE screen, not four.
//
// It was ten cards and about four phone screens long (measured 2026-11-02 at 375px), repeating what
// Security, Spam & held and Notifications each show in full. Now it answers "is my mailbox OK and is
// anything waiting for me" at the top, in four tiles that each lead to the page that can act on
// them, then today's mail, then the facts that govern what you can do (space, sending allowance,
// message size) — each with what sets it, kept visible (CLAUDE.md rule 1).
export function Overview() {
  const api = useMailboxApi();
  const { who } = useAuth();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const diag = useQuery({ queryKey: ["diagnostics"], queryFn: api.diagnostics, retry: false });
  const d = diag.data;
  const spam = useQuery({ queryKey: ["spam"], queryFn: api.spam, retry: false });
  const stats = useQuery({ queryKey: ["my-stats"], queryFn: () => api.myStats(30), retry: false });
  const activity = useQuery({ queryKey: ["me-activity"], queryFn: api.activity, retry: false, refetchInterval: 60000 });
  const rules = useQuery({ queryKey: ["filters"], queryFn: api.filters, retry: false });
  const deliver = useQuery({ queryKey: ["me-deliverability"], queryFn: api.deliverability, retry: false });

  const u = me.data?.usage;
  const measured = u != null && u.used_mb != null; // Dovecot answered — null is not 0 MB
  const pct = u?.percent ?? 0;
  // The limit actually enforced (limit_mb, null = unlimited): the mailbox's own size, or — with none
  // set — what is left of the account's allowance.
  const limitMB = u?.limit_mb ?? null;
  const hasQuota = limitMB != null && limitMB > 0;
  const held = spam.data?.held_now ?? 0;
  const devicesOn = d?.notifications?.subscriptions;

  // The diagnostics endpoint's flat facts, as checks. An unmeasurable one is neither pass nor fail.
  const checks: CheckItem[] = [];
  for (const [k, v] of Object.entries((d ?? {}) as Record<string, unknown>)) {
    if (v === null || typeof v === "object") continue;
    if (typeof v === "boolean") checks.push({ key: k, label: k.replace(/_/g, " "), state: v ? "ok" : "fail" });
    else checks.push({ key: k, label: k.replace(/_/g, " "), state: "ok", detail: String(v) });
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
        <Tile
          label="Storage"
          value={!measured ? "Not measured" : hasQuota ? `${pct}% used` : formatBytes((u?.used_mb ?? 0) * 1024 * 1024)}
          sub={hasQuota ? `of ${formatBytes((limitMB ?? 0) * 1024 * 1024)}` : measured ? "no limit" : undefined}
          tone={hasQuota && measured ? (pct >= 95 ? "crit" : pct >= 80 ? "warn" : undefined) : undefined}
        />
        <Tile
          to="/spam"
          label="Held for you"
          value={spam.isLoading ? "…" : `${held} message${held === 1 ? "" : "s"}`}
          sub={held > 0 ? "Review them" : "Nothing waiting"}
          tone={held > 0 ? "warn" : undefined}
        />
        <Tile
          to="/security"
          label="Two-factor"
          value={who?.two_factor === true ? "On" : who?.two_factor === false ? "Off" : "Not reported"}
          sub={who?.two_factor === false ? "Turn it on" : undefined}
          tone={who?.two_factor === false ? "warn" : undefined}
        />
        <Tile
          to="/notifications"
          label="New-mail alerts"
          value={devicesOn == null ? "—" : devicesOn > 0 ? `${devicesOn} device${devicesOn === 1 ? "" : "s"}` : "Not set up"}
          sub={devicesOn === 0 ? "Set up" : undefined}
        />
      </div>

      {hasQuota && measured && pct >= 95 && (
        <div role="alert" className="rounded-card border border-[color-mix(in_oklab,var(--ozp-crit)_40%,transparent)] bg-critsoft px-4 py-3 text-[13px] text-crit">
          Your mailbox is nearly full. Once it is full, new mail is refused and the sender is told.
        </div>
      )}

      {activity.data && <MailActivityDashboard a={activity.data} />}

      <Section title="Your mailbox" bodyClassName="p-0">
        <StateView loading={me.isLoading} error={me.error} errorContext="your mailbox">
          <div className="divide-y divide-line">
            <Row label="Address" value={<span className="font-mono">{me.data?.mailbox.address}</span>} />
            <Row
              label="Receiving mail"
              value={
                me.data ? (
                  me.data.mailbox.status === "active" ? <StatusPill tone="ok">yes</StatusPill> : <StatusPill tone="bad">suspended</StatusPill>
                ) : null
              }
              note={me.data && me.data.mailbox.status !== "active" ? "Mail sent to you is refused, and the sender is told." : undefined}
            />
            {hasQuota && measured && (
              <div className="px-4 py-3">
                <div className="flex items-baseline justify-between text-[13.5px]">
                  <span className="text-muted">Space used</span>
                  <span>
                    {formatBytes((u?.used_mb ?? 0) * 1024 * 1024)} of {formatBytes((limitMB ?? 0) * 1024 * 1024)}
                  </span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-bg2">
                  <div
                    className={cn("h-full rounded-full", pct >= 95 ? "bg-crit" : pct >= 80 ? "bg-warn" : "bg-accent")}
                    style={{ width: `${Math.min(100, pct)}%` }}
                  />
                </div>
                {u?.limit_note && <div className="mt-1 text-[12.5px] text-muted">{u.limit_note}</div>}
              </div>
            )}
            {d?.limits && (
              <Row
                label="You may send"
                value={`${d.limits.send_per_hour ?? "—"} an hour · ${d.limits.send_per_day ?? "—"} a day`}
                // What governs the number stays visible: a limit shared by the whole account means
                // something quite different from one of your own.
                note={d.limits.send_scope ? d.limits.send_scope + (d.limits.send_scope_note ? ` — ${d.limits.send_scope_note}` : "") : undefined}
              />
            )}
            {d?.limits?.max_message_mb ? (
              <Row
                label="Largest message"
                value={`${d.limits.max_message_mb} MB`}
                note={d.limits.max_attachment_mb ? `Attachments up to ${d.limits.max_attachment_mb} MB` : undefined}
              />
            ) : null}
            {me.data && (
              <Row
                label="App passwords"
                to="/security"
                value={`${me.data.app_passwords.count ?? "?"} of ${me.data.app_passwords.max}`}
                note={me.data.app_passwords.count == null ? "Could not be counted just now." : undefined}
              />
            )}
            {(stats.data?.by_direction?.inbound != null || stats.data?.by_direction?.outbound != null) && (
              <Row
                label="Last 30 days"
                value={`${stats.data?.by_direction?.inbound ?? 0} received · ${stats.data?.by_direction?.outbound ?? 0} sent`}
                note={(stats.data?.totals?.bounced ?? 0) > 0 ? `${stats.data?.totals?.bounced} bounced — refused by the far end after we accepted them` : undefined}
              />
            )}
          </div>
        </StateView>
      </Section>

      {(spam.data || rules.data) && (
        <Section title="Filtering" bodyClassName="p-0">
          <div className="divide-y divide-line">
            <Row
              label="Spam filter"
              to="/spam"
              value={spam.data?.mine?.spam_score != null ? `Your level: ${spam.data.mine.spam_score}` : "Your domain's level"}
              note={
                spam.data?.activity
                  ? `${spam.data.activity.sent_to_junk ?? 0} to Junk · ${spam.data.activity.refused ?? 0} refused${spam.data.activity.note ? ` (${spam.data.activity.note})` : ""}`
                  : undefined
              }
            />
            <Row
              label="Mail rules"
              to="/rules"
              value={rules.isLoading ? "…" : `${rules.data?.total ?? 0} rule${(rules.data?.total ?? 0) === 1 ? "" : "s"}`}
            />
          </div>
        </Section>
      )}

      {deliver.data && (
        <Section
          title="Is your mail set up correctly?"
          collapsible
          defaultOpen={false}
          id="mb-deliverability"
          hint={deliver.data.grade}
          bodyClassName="p-0"
        >
          <p className="px-4 pt-3 text-[13px] text-muted">
            For <span className="text-fg">{deliver.data.domain}</span>. If a check fails, ask your administrator to fix that item.
          </p>
          <div className="p-4">
            <CheckList
              items={(deliver.data.checks ?? []).map((c) => ({
                key: c.key ?? c.label ?? "",
                label: c.label ?? c.key ?? "",
                state: c.status === "pass" ? "ok" : c.status === "fail" ? "fail" : "warn",
                detail: c.detail,
              }))}
              summarise
            />
          </div>
        </Section>
      )}

      {checks.length > 0 && (
        <Technical>
          <Section title="What the server knows about your mail" collapsible defaultOpen={false} id="mb-diag" hint="technical checks" bodyClassName="p-0">
            <CheckList items={checks} summarise />
          </Section>
        </Technical>
      )}

      <Details summary="About these figures" className="px-1">
        <p>
          Sign-ins to this app and mail apps connected to your mailbox are listed under Security. Signing out here does
          not disconnect a mail app; changing your mail password does.
        </p>
        <p>The 30-day counts come from the server's mail log; days before it started are absent, not zero.</p>
      </Details>
    </div>
  );
}

/** A status tile; with `to`, the whole tile is the way to the page that acts on it. */
function Tile({ label, value, sub, tone, to }: { label: string; value: React.ReactNode; sub?: string; tone?: "warn" | "crit"; to?: string }) {
  const body = (
    <>
      <div className="flex items-center justify-between gap-1 text-[12px] text-muted">
        {label}
        {to && <ChevronRight size={14} aria-hidden className="shrink-0 text-faint" />}
      </div>
      <div className={cn("mt-1 text-[16px] leading-snug font-semibold", tone === "crit" ? "text-crit" : tone === "warn" ? "text-warn" : "text-fg")}>
        {value}
      </div>
      {sub && <div className="mt-0.5 text-[12px] text-muted">{sub}</div>}
    </>
  );
  const cls = "block min-h-[84px] rounded-card border border-line bg-surface px-3.5 py-3";
  return to ? (
    <Link to={to} className={cn(cls, "hover:border-[var(--ozp-accent-line)]")}>
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  );
}

/** One fact, and under it what governs the fact. With `to`, the row leads to where it is changed. */
function Row({ label, value, note, to }: { label: string; value: React.ReactNode; note?: string; to?: string }) {
  const inner = (
    <>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="text-[13.5px] text-muted">{label}</span>
        <span className="ml-auto inline-flex items-center gap-1 text-right text-[13.5px] [overflow-wrap:anywhere]">
          {value}
          {to && <ChevronRight size={14} aria-hidden className="text-faint" />}
        </span>
      </div>
      {note && <div className="mt-1 text-[12.5px] leading-relaxed text-muted">{note}</div>}
    </>
  );
  return to ? (
    <Link to={to} className="block px-4 py-3 hover:bg-surface2">
      {inner}
    </Link>
  ) : (
    <div className="px-4 py-3">{inner}</div>
  );
}
